import React, { useEffect, useState } from "react";
import {
  Container,
  Text,
  Button,
  Stack,
  Modal,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import BingoGrid from "../components/BingoGrid";
import CaretRightFilled from "bingo/ui/CaretRightFilled";
import CaretLeftFilled from "bingo/ui/CaretLeftFilled";
import { useUpdatePlayerMutation } from "quiz/api/players.endpoints";

import { bingoGameService } from "services/services";
import { generateThreeUniqueGrids } from "bingo/utils/generateThreeUniqueGrids";
import { insertZeros } from "bingo/utils/insertZeros";
import { removeZeros } from "bingo/utils/removeZeros";
import { DEFAULT_GOLD_COLOR } from "_graphic_design";
import {
  BingoGameViewProps,
  ScreenType,
} from "bingo/play/views/types/bingo.game.view.types";
import { config } from "../../../config";
import { usePlayerBingoGameStatus } from "bingo/hooks/usePlayerBingoGameStatus";
import { PlayerInfoState } from "bingo/play/views/types/bingo.game.view.types";

import { useLocation } from "react-router-dom";
import { Event } from "event/types/event.model";
import { Footer } from "event/play/components/Footer";
import { EventHeader } from "event/play/components/EventHeader";
export const PlayBingoGameView: React.FC<BingoGameViewProps> = ({
  bingoGame,
  playerId,
  partitionId,
  playerInfo,
}) => {
  //state
  const [currentScreen, setCurrentScreen] = useState<ScreenType>("P1");
  const [currentGridIndex, setCurrentGridIndex] = useState<number>(0);
  const [threeGridChoiceOptions, setThreeGridChoiceOptions] = useState<
    number[][]
  >([]); // array of 1D arrays
  const theme = useMantineTheme();
  const [isWinModalOpen, setWinModalOpen] = useState(false);
  const [userChosenGrid, setUserChosenGrid] = useState<number[]>([]);
  const [savedEntriesState, setSavedEntriesState] = useState<
    number[] | undefined
  >([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [isCheckingTap, setIsCheckingTap] = useState(false);
  const [endScreenMessage, setEndScreenMessage] = useState("Game has closed");
  const [playerInfoState, setPlayerInfoState] = useState<PlayerInfoState>({
    accessNumber: playerInfo?.accessNumber,
    email: playerInfo?.email,
    firstName: playerInfo?.firstName,
    lastName: playerInfo?.lastName,
    phoneNumber: playerInfo?.phoneNumber,
    rankNumber: playerInfo?.rankNumber,
    seatNumber: playerInfo?.seatNumber,
    id: playerId,
    partitionId: partitionId,
    creationTime: new Date().toISOString(),
  });
  const location = useLocation();
  const _state = location.state as { event?: Event } | undefined;
  const event = _state?.event;

  //top level hooks
  const [updatePlayer, { isLoading: isUpdatingPlayer }] =
    useUpdatePlayerMutation();

  const { player, isLoading, refetch } = usePlayerBingoGameStatus(
    playerId,
    partitionId,
  );
  const playerChosenGrids = player?.bingoGridNumbers;
  const playerEntriedNumbers = player?.bingoEntries;
  const playerGameCompletionStatus = player?.bingoGameCompleted;
  const playerRank = player?.bingoGameRank;

  //screens as per game state
  useEffect(() => {
    // Update playerInfoState whenever player data changes
    if (player) {
      setPlayerInfoState((prevState) => ({
        ...prevState,
        bingoGridNumbers: playerChosenGrids,
        bingoEntries: playerEntriedNumbers,
        bingoGameCompleted: playerGameCompletionStatus,
      }));
    }
  }, [
    player,
    playerChosenGrids,
    playerEntriedNumbers,
    playerGameCompletionStatus,
  ]);

  useEffect(() => {
    console.log("player", player);
    console.log("playerChosenGrids:", playerChosenGrids);
    console.log("playerEntriedNumbers:", playerEntriedNumbers);
    console.log("playerGameCompletionStatus:", playerGameCompletionStatus);
    console.log("Bingo Game Status", bingoGame.status);

    const bingoGameStatus = bingoGame.status;

    if (bingoGameStatus === "CLOSED") {
      console.log("Game closed");
      setEndScreenMessage(
        bingoGame.bingoTemplateSnapshot.frontOfficeMessages.gameEnded,
      );
      setCurrentScreen("P4");
      return;
    }

    //condition when player has grid but game not started - allow the player

    //test this condition
    if (bingoGameStatus === "IN_PROGRESS" && !playerChosenGrids) {
      console.log("game in progress, player has no grid");
      setEndScreenMessage(
        bingoGame.bingoTemplateSnapshot.frontOfficeMessages.gameAlreadyStarted,
      );
      setCurrentScreen("P4");
      return;
    }

    //add condition to check if player is winner for P4

    //game complete signal from server
    if (playerGameCompletionStatus) {
      if (playerRank === "winner") {
        setEndScreenMessage(
          bingoGame.bingoTemplateSnapshot.frontOfficeMessages.winnerPrivate,
        );
        setCurrentScreen("P4");
        return;
      } else if (playerRank === "runner-up") {
        setEndScreenMessage(
          (
            bingoGame.bingoTemplateSnapshot.frontOfficeMessages
              ?.winnerTooLatePrivate ??
            "Aïe ! Quelqu'un a confirmé son bingo avant vous et vous a chipé la victoire !\n\n🔴🔵\n\nMerci pour votre passion et votre engagement. On se retrouve très vite pour une nouvelle partie !"
          ).replace(/\\n/g, "\n"),
        );
        setCurrentScreen("P4");
        return;
      } else if (playerRank === "loser") {
        setEndScreenMessage(
          bingoGame.bingoTemplateSnapshot.frontOfficeMessages.looserPrivate,
        );
        setCurrentScreen("P4");
        return;
      }

      setEndScreenMessage(
        bingoGame.bingoTemplateSnapshot.frontOfficeMessages.looserPrivate,
      );
      setCurrentScreen("P4");
      return;
    }

    // case of chosen numbers matching the draw numbers is handled inside bingo grid, no separate implmentation here needed

    //if grid chosen, and player has some numbers tapped
    if (
      playerChosenGrids &&
      playerEntriedNumbers &&
      playerEntriedNumbers.length > 0
    ) {
      setUserChosenGrid(insertZeros(playerChosenGrids));
      setSavedEntriesState(playerEntriedNumbers);
      setCurrentScreen("P3");
      return;
    }
    //if only grid is chosen
    if (playerChosenGrids && playerChosenGrids.length > 0) {
      setUserChosenGrid(insertZeros(playerChosenGrids));
      setCurrentScreen("P3");
      return;
    }
  }, [
    playerRank,
    playerGameCompletionStatus,
    playerChosenGrids,
    playerEntriedNumbers,
    bingoGame.status,
    bingoGame.bingoTemplateSnapshot.frontOfficeMessages.gameEnded,
    bingoGame.bingoTemplateSnapshot.frontOfficeMessages.gameAlreadyStarted,
    bingoGame.bingoTemplateSnapshot.frontOfficeMessages.winnerPrivate,
    player,
    bingoGame.bingoTemplateSnapshot.frontOfficeMessages,
  ]);

  useEffect(() => {
    if (!isLoading) {
      console.log("Player data loaded, clearing loading state");
      setDataLoading(false);
    }
  }, [isLoading]);

  //Generate 3 grids
  useEffect(() => {
    setThreeGridChoiceOptions(generateThreeUniqueGrids()); // this makes 3 grids
  }, []);

  //start Game and goto P2
  const startGame = () => {
    setCurrentScreen("P2");
  };
  //handle grid selection

  const handleGridSelection = async (
    selectedGrid: number[],
    playerId: string,
  ) => {
    try {
      const updatedPlayerInfo = {
        ...playerInfoState,
        bingoGameId: bingoGame.id,
        bingoGridNumbers: removeZeros(selectedGrid),
      };

      setPlayerInfoState(updatedPlayerInfo);
      console.log("payload : ", updatedPlayerInfo);

      // Wait for the updatePlayer mutation to complete
      const response = await updatePlayer(updatedPlayerInfo as any);

      if ("data" in response) {
        //const userSelectedGridFromServer = response.data.bingoGridNumbers;
        console.log(
          "success in adding bingogrid, playerInfo now is ",
          response,
        );

        await refetch();
      } else {
        console.error("Failed to update player:", response.error);
      }
    } catch (err) {
      console.error("Failed to update player:", err);
    }
  };

  //goto next grid
  const nextGrid = (): void => {
    setCurrentGridIndex((prev) => (prev + 1) % threeGridChoiceOptions.length);
  };
  //goto prev grid
  const prevGrid = (): void => {
    setCurrentGridIndex(
      (prev) =>
        (prev - 1 + threeGridChoiceOptions.length) %
        threeGridChoiceOptions.length,
    );
  };
  //handle success
  const handleSuccess = (): void => {
    setWinModalOpen(true);
  };
  //modal close
  const handleCloseModal = (): void => {
    setWinModalOpen(false);
    checkWinner();
    setCurrentScreen("P4");
  };
  //check winner
  // const checkWinner = async () => {
  //   console.log(savedEntriesState?.join(","));
  //   const testService = async () => {
  //     const response = bingoGameService.checkWinner({
  //       bingoEntries: savedEntriesState!.join(","),
  //       playerId: playerId,
  //       partitionId: partitionId,
  //       bingoGameId: bingoGame.id,
  //     });
  //     console.log("check winner", response);
  //   };
  //   await testService();

  //   //marking game close
  //   try {
  //     const updatedPlayerInfo = {
  //       ...playerInfoState,
  //       bingoGameCompleted: true,
  //     };

  //     const response = await updatePlayer(updatedPlayerInfo as any);

  //     if ("data" in response) {
  //       const gameCompletedResponse = response.data.bingoGameCompleted;
  //       await refetch();
  //     } else {
  //       // Handle the error case
  //       console.error("Failed to update player:", response.error);
  //     }
  //   } catch (err) {
  //     console.error("Error completing game:", err);
  //   }
  // };
  //check winner
  const checkWinner = async () => {
    try {
      // First, make sure we have the latest entries
      if (!savedEntriesState || savedEntriesState.length === 0) {
        console.error("No entries to submit for winner check");
        return;
      }

      console.log("Checking winner with entries:", savedEntriesState.join(","));

      // Call the checkWinner service
      let winnerCheckResponse: any;
      try {
        winnerCheckResponse = (await bingoGameService.checkWinner({
          bingoEntries: savedEntriesState.join(","),
          playerId: playerId,
          partitionId: partitionId,
          bingoGameId: bingoGame.id,
        })) as any;
        console.log("Check winner response:", winnerCheckResponse);

        if (winnerCheckResponse.status === "winner") {
          setEndScreenMessage(
            bingoGame.bingoTemplateSnapshot.frontOfficeMessages.winnerPrivate,
          );
          setCurrentScreen("P4");
        } else if (winnerCheckResponse.status === "runner-up") {
          setEndScreenMessage(
            bingoGame.bingoTemplateSnapshot.frontOfficeMessages
              .winnerTooLatePrivate || "You are runners up!",
          );
          setCurrentScreen("P4");
        }
      } catch (err) {
        console.error("Network error while checking winner", err);
        alert("Winner check failed on our side!");
        return;
      }

      // Prepare the complete player info payload with all fields
      const completePlayerPayload = {
        ...playerInfoState,
        bingoGameCompleted: true,
        bingoGridNumbers: playerInfoState.bingoGridNumbers || playerChosenGrids,
        bingoEntries: playerInfoState.bingoEntries || playerEntriedNumbers,
        id: playerId,
        partitionId: partitionId,
        accessNumber: playerInfo?.accessNumber,
        email: playerInfo?.email,
        firstName: playerInfo?.firstName,
        lastName: playerInfo?.lastName,
        phoneNumber: playerInfo?.phoneNumber,
        rankNumber: playerInfo?.rankNumber,
        seatNumber: playerInfo?.seatNumber,
        creationTime: playerInfoState.creationTime || new Date().toISOString(),
        bingoGameId: bingoGame.id,
        bingoGameRank:
          (winnerCheckResponse as any).status === "winner"
            ? "winner"
            : (winnerCheckResponse as any).status === "runner-up"
              ? "runner-up"
              : "loser",
      };

      console.log("Submitting complete player payload:", completePlayerPayload);

      // Update the player with the complete payload
      const response = await updatePlayer(completePlayerPayload as any);

      if ("data" in response) {
        console.log("Game completed successfully:", response.data);
        await refetch();
      } else {
        console.error(
          "Failed to update player on game completion:",
          response.error,
        );
      }
    } catch (err) {
      console.error("Error in checkWinner process:", err);
    }
  };
  const renderScreen = (): JSX.Element | null => {
    switch (currentScreen) {
      case "P1":
        return (
          <Container className="flex flex-col items-center justify-center mb-8 h-full">
            <Text
              className="text-center mb-2 mt-2 whitespace-pre-line"
              style={{
                color:
                  bingoGame.bingoTemplateSnapshot.privateScreenSettings
                    .messagesColor,
                fontSize:
                  bingoGame.bingoTemplateSnapshot.privateScreenSettings
                    .messagesSize,
              }}
            >
              {bingoGame.bingoTemplateSnapshot?.frontOfficeMessages.gameWelcome}
            </Text>
            <Button
              style={{
                backgroundColor:
                  bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                    ?.buttonBackgroundColor,
                color:
                  bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                    ?.buttonsLabelColor,
                borderColor:
                  bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                    ?.buttonsBorderColor,
              }}
              className="mt-8"
              onClick={startGame}
            >
              {
                bingoGame.bingoTemplateSnapshot?.frontOfficeMessages
                  .gameWelcomeButtonLabel
              }
            </Button>
          </Container>
        );

      case "P2":
        return (
          <div className="flex flex-col items-center w-screen mb-4 p-8">
            <div className="flex items-center justify-center w-full">
              <Button
                onClick={prevGrid}
                className="bg-transparent text-white text-4xl md:static absolute left-[-5px] p-0"
              >
                <CaretLeftFilled width={64} height={64} />
              </Button>

              <BingoGrid
                onWin={() => {}}
                numbers={threeGridChoiceOptions[currentGridIndex]}
                isPreviewMode={true}
                topText={`${currentGridIndex + 1}/${threeGridChoiceOptions.length}`}
                bingoGame={bingoGame}
                playerId={playerId}
                partitionId={partitionId}
                playerInfoState={playerInfoState}
              />

              <Button
                onClick={nextGrid}
                className="bg-transparent text-white text-4xl md:static absolute right-[5px] p-0"
              >
                <CaretRightFilled width={64} height={64} />
              </Button>
            </div>

            <Button
              style={{
                backgroundColor:
                  bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                    ?.buttonBackgroundColor,
                color:
                  bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                    ?.buttonsLabelColor,
                borderColor:
                  bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                    ?.buttonsBorderColor,
              }}
              className="mt-8"
              onClick={() =>
                handleGridSelection(
                  threeGridChoiceOptions[currentGridIndex],
                  playerId,
                )
              }
            >
              {
                bingoGame.bingoTemplateSnapshot?.frontOfficeMessages
                  .gridChoiceButtonLabel
              }
            </Button>
          </div>
        );

      case "P3":
        return (
          <div className="flex flex-col items-center w-screen p-8 mb-4">
            <BingoGrid
              onWin={handleSuccess}
              numbers={userChosenGrid} // Since we store only one grid now
              bingoGame={bingoGame}
              playerId={playerId}
              partitionId={partitionId}
              entries={playerEntriedNumbers || []}
              refetch={refetch}
              setIsCheckingTap={setIsCheckingTap}
              playerInfoState={playerInfoState}
              setEndScreenMessage={setEndScreenMessage}
              setCurrentScreen={setCurrentScreen}
            />
          </div>
        );

      case "P4":
        return (
          <Container className="flex flex-col items-center mb-8 justify-center">
            <Text
              className="text-center mt-2 mb-2 whitespace-pre-line"
              style={{
                color:
                  bingoGame.bingoTemplateSnapshot.privateScreenSettings
                    .messagesColor,
                fontSize:
                  bingoGame.bingoTemplateSnapshot.privateScreenSettings
                    .messagesSize,
              }}
            >
              {endScreenMessage}
            </Text>
            <Button
              style={{
                backgroundColor:
                  bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                    ?.buttonBackgroundColor,
                color:
                  bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                    ?.buttonsLabelColor,
                borderColor:
                  bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                    ?.buttonsBorderColor,
              }}
              className="mt-8"
              onClick={() => {
                window.location.href = `${config.webAppRoot}/event/${event?.id}`;
              }}
            >
              {
                bingoGame.bingoTemplateSnapshot?.frontOfficeMessages
                  .welcomeReturnButtonLabel
              }
            </Button>
          </Container>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {(isLoading || dataLoading || isUpdatingPlayer || isCheckingTap) && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <Loader type="bars" color="#e30613" size="xl" />
        </div>
      )}

      {!isLoading && !dataLoading && (
        <div>
          <Stack
            className="min-h-screen justify-start"
            justify="center"
            align={"center"}
          >
            <EventHeader />
            {renderScreen()}
          </Stack>
          <Footer />
        </div>
      )}

      {/* Win Modal */}
      <Modal
        opened={isWinModalOpen}
        onClose={handleCloseModal}
        centered
        radius={0}
        size={"md"}
        withCloseButton={false}
        overflow="inside"
        closeOnClickOutside={false}
      >
        <p
          className="text-center text-4xl "
          style={{ color: DEFAULT_GOLD_COLOR, fontFamily: theme.fontFamily }}
        >
          Bingo !
        </p>
        <div className="flex justify-center mt-6">
          <Button
            style={{
              backgroundColor:
                bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                  ?.buttonBackgroundColor,
              color:
                bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                  ?.buttonsLabelColor,
              borderColor:
                bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                  ?.buttonsBorderColor,
            }}
            onClick={handleCloseModal}
          >
            Cliquez pour remporter la victoire
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PlayBingoGameView;
